/**
 * WelcomePackGate
 * ────────────────────────────────────────────────────────────────────────
 * Lead-capture gate that wraps the homepage Welcome Pack actions
 * (Read on screen / Open in new tab / Download). Visitors must enter
 * name + email + role before any action is unlocked. Once submitted, an
 * unlock token is persisted to localStorage so they're never re-asked
 * on this browser.
 *
 * Public surface (returned by `useWelcomePackGate`):
 *   • isUnlocked          — boolean reflecting localStorage state.
 *   • requireUnlock(fn, intent) — runs `fn` if unlocked, else opens the
 *     gate modal and runs `fn` after a successful submit.
 *   • GateModal           — renders the gate. Drop into the page tree once.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Lock } from 'lucide-react';

const STORAGE_KEY = 'hibff_welcome_pack_unlocked';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'parent', label: 'Parent / Guardian' },
  { value: 'teen', label: 'Teen (13–17)' },
  { value: 'educator', label: 'Educator / Counsellor' },
  { value: 'school', label: 'School / District Lead' },
  { value: 'other', label: 'Other' },
];

const readUnlock = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.email ? parsed : null;
  } catch {
    return null;
  }
};

export const useWelcomePackGate = () => {
  const [unlock, setUnlock] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  // Holds the action queued while the gate is open.
  const pendingRef = useRef(null);

  useEffect(() => {
    setUnlock(readUnlock());
  }, []);

  const requireUnlock = useCallback((fn, intent) => {
    const current = readUnlock();
    if (current) {
      // Best-effort backend ping so repeat unlocks bump unlock_count.
      // We swallow errors — user is already authorised by localStorage.
      try {
        fetch(`${API_URL}/api/public/welcome-pack/lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...current, intent }),
        }).catch(() => {});
      } catch {
        // noop
      }
      fn();
      return;
    }
    pendingRef.current = { fn, intent };
    setIsOpen(true);
  }, []);

  const onSubmitSuccess = useCallback((lead) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lead));
    } catch {
      // ignore quota errors
    }
    setUnlock(lead);
    setIsOpen(false);
    const queued = pendingRef.current;
    pendingRef.current = null;
    if (queued && typeof queued.fn === 'function') queued.fn();
  }, []);

  const onCancel = useCallback(() => {
    pendingRef.current = null;
    setIsOpen(false);
  }, []);

  return {
    isUnlocked: !!unlock,
    requireUnlock,
    GateModal: (
      <WelcomePackGateModal
        isOpen={isOpen}
        intent={pendingRef.current?.intent}
        onSuccess={onSubmitSuccess}
        onCancel={onCancel}
      />
    ),
  };
};

const WelcomePackGateModal = ({ isOpen, intent, onSuccess, onCancel }) => {
  const [form, setForm] = useState({ name: '', email: '', role: 'parent' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset form whenever the modal reopens — visitors won't expect stale state.
  useEffect(() => {
    if (isOpen) {
      setError('');
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const name = form.name.trim();
    const email = form.email.trim();
    const role = form.role;
    if (!name) return setError('Please enter your name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Please enter a valid email.');
    if (!role) return setError('Please select your role.');

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/welcome-pack/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, role, intent: intent || null }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      onSuccess({ name, email, role });
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center px-4"
      data-testid="welcome-pack-gate"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-[#1A1209]/70 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: '#FFF5EA' }}
            >
              <Lock size={18} style={{ color: '#F5831F' }} />
            </div>
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-widest"
                style={{ color: '#F5831F' }}
              >
                Welcome Pack
              </p>
              <h3
                className="text-base font-bold leading-tight"
                style={{ color: '#1A1209' }}
              >
                One quick step before you read
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
            aria-label="Close"
            data-testid="welcome-pack-gate-close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            We share the Parent Compact and Teen Digital Compact with families
            who care about how their teen practises. Tell us who you are so we
            can tailor anything we send next. We never spam.
          </p>

          <div>
            <label
              htmlFor="wp-gate-name"
              className="block text-xs font-semibold text-gray-700 mb-1"
            >
              Your name
            </label>
            <input
              id="wp-gate-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Jane Smith"
              autoComplete="name"
              required
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F5831F] focus:border-transparent"
              data-testid="welcome-pack-gate-name"
            />
          </div>

          <div>
            <label
              htmlFor="wp-gate-email"
              className="block text-xs font-semibold text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="wp-gate-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="jane@example.com"
              autoComplete="email"
              required
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F5831F] focus:border-transparent"
              data-testid="welcome-pack-gate-email"
            />
          </div>

          <div>
            <label
              htmlFor="wp-gate-role"
              className="block text-xs font-semibold text-gray-700 mb-1"
            >
              I am a…
            </label>
            <select
              id="wp-gate-role"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              required
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#F5831F] focus:border-transparent"
              data-testid="welcome-pack-gate-role"
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {error && (
            <p
              className="text-xs text-red-600"
              data-testid="welcome-pack-gate-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-5 py-3 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-60"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="welcome-pack-gate-submit"
          >
            {submitting ? 'Unlocking…' : 'Read the Welcome Pack'}
          </button>

          <p className="text-[11px] text-gray-500 text-center leading-relaxed">
            By continuing you agree to our{' '}
            <a href="/privacy" className="underline hover:text-gray-700" target="_blank" rel="noreferrer">privacy policy</a>.
            HiBFF will never sell your information.
          </p>
        </form>
      </div>
    </div>
  );
};

export default useWelcomePackGate;
